import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import RadioGroup from '@cloudscape-design/components/radio-group';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { ALL_CANCELLATION_REASONS, type CancellationReason } from '@etiquetachef/types';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

export type CategorizeReasonDialogProps = {
  open: boolean;
  onClose: () => void;
  tenantName: string;
  suggested: CancellationReason;
  initial: CancellationReason | null;
  onSave: (reason: CancellationReason) => Promise<void>;
};

/**
 * Modal para escolher o motivo de um cancelamento, pré-selecionado com o
 * motivo já gravado ou, sem ele, com a sugestão heurística.
 *
 * Origem: sem issue — commit b9c02cd45, 2026-05-04 (categorizar motivo do
 * churn).
 *
 * Contrato: não grava nada sozinho — chama `onSave(motivo)` e fecha no
 * sucesso; falha → mensagem inline + `reportNonFatal('admin_churn_categorize_failed')`.
 *
 * Usado por: `ChurnTable.tsx` (tela `/dashboard/churn`), que passa
 * `persistReason` como `onSave`.
 */
export function CategorizeReasonDialog({
  open,
  onClose,
  tenantName,
  suggested,
  initial,
  onSave,
}: CategorizeReasonDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<CancellationReason>(initial ?? suggested);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await onSave(selected);
      onClose();
    } catch (err) {
      reportNonFatal('admin_churn_categorize_failed', {
        reason: selected,
        ...serializeError(err),
      });
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      visible={open}
      onDismiss={onClose}
      header={t('churn.categorize.title', { tenant: tenantName })}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSave();
              }}
              loading={saving}
            >
              {t('common.save')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('churn.categorize.suggested', { reason: t(`churn.reason.${suggested}`) })}
        </Box>
        <RadioGroup
          value={selected}
          onChange={({ detail }): void => {
            setSelected(detail.value as CancellationReason);
          }}
          items={ALL_CANCELLATION_REASONS.map((reason) => ({
            value: reason,
            label: t(`churn.reason.${reason}`),
          }))}
        />
        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
